/**
 * 🟥 CUADRO NEGRO — Explorador de Archivos Generados.
 *
 * Drawer lateral derecho de SOLO LECTURA que muestra la estructura del
 * proyecto generado (árbol de carpetas + archivos con iconos). NO abre
 * editores, NO renderiza código, NO ejecuta nada. Simplemente lee la
 * estructura ya generada por el Orquestador.
 *
 * UX:
 * - Width: 380px (cumple cuadro)
 * - Fondo: #0F172A (consistente Workspace)
 * - Overlay oscuro detrás → click cierra el drawer
 * - Botón X arriba a la derecha
 * - Scroll vertical estilizado (clase `gen-files-scroll`)
 * - Iconos: lucide Folder / FileText (alternativa a emoji)
 */
import React, { useMemo, useEffect } from 'react';
import { Folder, FileText, X } from 'lucide-react';
import { parseProjectFiles } from '../utils/downloadProject';
import './GeneratedFilesDrawer.css';

/**
 * Construye un árbol jerárquico a partir de la lista plana de archivos.
 * Cada nodo: { name, isFolder, children?, path?, language? }
 */
const buildTree = (files) => {
  const root = { name: '', isFolder: true, children: {} };
  for (const file of files) {
    const parts = (file.path || '').split('/').filter(Boolean);
    let cursor = root;
    parts.forEach((part, idx) => {
      const isLast = idx === parts.length - 1;
      if (!cursor.children[part]) {
        cursor.children[part] = isLast
          ? { name: part, isFolder: false, path: file.path, language: file.language, content: file.content || '' }
          : { name: part, isFolder: true, children: {} };
      }
      cursor = cursor.children[part];
    });
  }
  // Convert nested objects to sorted arrays (folders first, then files A→Z).
  const toArray = (node) => {
    if (!node.isFolder) return node;
    const arr = Object.values(node.children).map(toArray);
    arr.sort((a, b) => {
      if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return { ...node, children: arr };
  };
  return toArray(root).children;
};

// 🟥 CUADRO NEGRO — Formateador de tamaño del badge.
// < 1024 B  → "{n} B"
// < 1 MB    → "{n.x} KB"
// >= 1 MB   → "{n.x} MB"
const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const TreeNode = ({ node, depth = 0 }) => {
  if (node.isFolder) {
    return (
      <div className="gen-files-folder" data-testid="gen-files-folder">
        <div
          className="gen-files-row gen-files-row-folder"
          style={{ paddingLeft: `${12 + depth * 14}px` }}
        >
          <Folder className="w-4 h-4 mr-2 shrink-0" style={{ color: '#7dd3fc' }} />
          <span className="text-zinc-200 text-sm font-medium truncate">{node.name}</span>
        </div>
        <div className="gen-files-children">
          {node.children.map((child) => (
            <TreeNode key={(child.path || '') + child.name} node={child} depth={depth + 1} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className="gen-files-row gen-files-row-file"
      style={{ paddingLeft: `${12 + depth * 14}px` }}
      data-testid="gen-files-file"
    >
      <FileText className="w-4 h-4 mr-2 shrink-0" style={{ color: '#94a3b8' }} />
      <span className="text-zinc-300 text-sm truncate flex-1 min-w-0" title={node.path}>{node.name}</span>
      <span
        className="gen-files-size shrink-0"
        data-testid="gen-files-size"
        title={`${(node.content || '').length} bytes`}
      >
        {formatSize((node.content || '').length)}
      </span>
    </div>
  );
};

const GeneratedFilesDrawer = ({ open, onClose, messages = [], language = 'es' }) => {
  // Extrae los archivos de TODOS los mensajes asistente de la conversación.
  // Si un archivo aparece varias veces (refactor / regenerado), tomamos la
  // ULTIMA aparicion como version vigente.
  const tree = useMemo(() => {
    if (!open) return [];
    const allFiles = [];
    const seen = new Map();
    for (const msg of messages) {
      if (msg?.role === 'assistant' && typeof msg.content === 'string') {
        const parsed = parseProjectFiles(msg.content);
        for (const f of parsed) {
          if (f.path) seen.set(f.path, f);
        }
      }
    }
    for (const f of seen.values()) allFiles.push(f);
    return buildTree(allFiles);
  }, [open, messages]);

  const fileCount = useMemo(() => {
    let n = 0;
    const walk = (nodes) => {
      for (const n2 of nodes) {
        if (n2.isFolder) walk(n2.children);
        else n++;
      }
    };
    walk(tree);
    return n;
  }, [tree]);

  // ESC cierra el drawer (UX accesible)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="gen-files-overlay" onClick={onClose} data-testid="gen-files-overlay">
      <aside
        className="gen-files-drawer"
        onClick={(e) => e.stopPropagation()}
        data-testid="gen-files-drawer"
        role="dialog"
        aria-label={language === 'es' ? 'Archivos Generados' : 'Generated Files'}
      >
        <header className="gen-files-header">
          <div>
            <h3 className="text-white text-base font-semibold">
              {language === 'es' ? 'Archivos Generados' : 'Generated Files'}
            </h3>
            <p className="text-zinc-500 text-xs mt-0.5" data-testid="gen-files-count">
              {fileCount} {language === 'es' ? 'archivo(s)' : 'file(s)'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="gen-files-close-btn"
            aria-label={language === 'es' ? 'Cerrar' : 'Close'}
            data-testid="gen-files-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="gen-files-tree gen-files-scroll">
          {tree.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-12">
              {language === 'es' ? 'No se detectaron archivos generados.' : 'No generated files detected.'}
            </p>
          ) : (
            tree.map((node) => (
              <TreeNode key={(node.path || '') + node.name} node={node} depth={0} />
            ))
          )}
        </div>
      </aside>
    </div>
  );
};

export default GeneratedFilesDrawer;
